/**
 * Auth Store Utilities
 * AuthStore에서 API 호출에 필요한 AuthConfig를 추출하는 유틸리티 함수들
 */

import { useAuthStore } from '../store/authStore';

/**
 * API 호출에 필요한 인증 정보 인터페이스
 */
export interface AuthConfig {
  userToken: string;
  userEmail: string;
  jwtToken: string;
}

/**
 * AuthStore에서 AuthConfig 정보를 추출
 * @returns AuthConfig 객체 또는 null (인증되지 않은 경우)
 */
export function getAuthConfigFromStore(): AuthConfig | null {
  const state = useAuthStore.getState();
  
  // 인증 상태 및 필수 정보 확인
  if (!state.isAuthenticated || 
      !state.userToken || 
      !state.jwtToken || 
      !state.subscribeInfo?.userEmail) {
    return null;
  }
  
  return {
    userToken: state.userToken,
    userEmail: state.subscribeInfo.userEmail,
    jwtToken: state.jwtToken
  };
}

/**
 * 인증이 완료되었는지 확인
 * @returns true if authenticated and all required auth data is available
 */
export function isAuthConfigured(): boolean {
  return getAuthConfigFromStore() !== null;
}

/**
 * 인증 정보가 유효한지 확인 (추가 검증)
 * @returns true if auth config is valid
 */
export function isAuthConfigValid(): boolean {
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    return false;
  }
  
  // 토큰 형식 기본 검증
  return authConfig.userToken.length > 0 && 
         authConfig.jwtToken.length > 0 && 
         authConfig.userEmail.includes('@');
}

/**
 * API 호출용 인증 헤더 생성
 * @returns HTTP headers for API calls
 */
export function getAuthHeaders(): Record<string, string> {
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error('Authentication required. Please login first.');
  }
  
  return {
    'Authorization': `Bearer ${authConfig.jwtToken}`,
    'Content-Type': 'application/json'
  };
}

/**
 * 인증 에러 시 표준 에러 메시지 반환
 * @returns Standard authentication error message
 */
export function getAuthErrorMessage(): string {
  return 'Authentication required. Please login first.';
}